import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, useAuth } from "@clerk/clerk-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Plus,
  Trash2,
  GraduationCap,
  Briefcase,
  ChevronLeft,
  ShieldCheck,
  UserCheck,
  Lock,
  Loader2,
  Info,
} from "lucide-react";
import Navbar from "@/components/landing page/Navbar";
import Footer from "@/components/landing page/Footer";
import { apiFetch } from "@/src/lib/api-fetch";
import { validateName, validateEmail } from "@/lib/validation";

const INDUSTRIES = [
  "Technology & Software",
  "Financial Services & Banking",
  "Healthcare & Pharma",
  "Manufacturing & Industrial",
  "Consulting & Professional Services",
  "Retail & E-Commerce",
  "Telecommunications",
  "Education & Higher Ed",
  "Government & Non-Profit",
  "Other",
];

const COMPANY_SIZES = [
  "1-15 Employees",
  "16-50 Employees",
  "51-200 Employees",
  "201-500 Employees",
  "501-1000 Employees",
  "1000+ Enterprise",
];

const SUGGESTED_SKILLS = [
  "Generative AI & LLMs",
  "AI Strategy & Governance",
  "Python for Machine Learning",
  "Data Analytics & BI",
  "Cloud Architecture",
  "Prompt Engineering",
  "Cybersecurity in AI",
];

export default function B2BRegisterPage() {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded: isAuthLoaded } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();

  const [formData, setFormData] = useState({
    organization_name: "",
    slug: "",
    admin_name: user?.fullName || "",
    admin_email: user?.primaryEmailAddress?.emailAddress || "",
    industry: "Technology & Software",
    company_size: "51-200 Employees",
    description: "",
    allowed_domains: [] as string[],
    newDomainInput: "",
    expected_skills: ["Generative AI & LLMs", "AI Strategy & Governance"] as string[],
    target_roles: ["Software Engineer", "Data Scientist", "Product Manager"] as string[],
    departments: ["Engineering", "Product", "Data", "Sales", "HR", "Operations"] as string[],
    newSkillInput: "",
    newDeptInput: "",
  });

  // Automatically hydrate admin details and domain from authenticated user if available
  useEffect(() => {
    if (user) {
      const email = user.primaryEmailAddress?.emailAddress || "";
      const name = user.fullName || "";
      const emailDomain = email.includes("@") ? email.split("@")[1]?.toLowerCase() : "";

      setFormData((prev) => {
        const domains = [...prev.allowed_domains];
        if (
          domains.length === 0 &&
          emailDomain &&
          !["gmail.com", "yahoo.com", "hotmail.com", "outlook.com"].includes(emailDomain)
        ) {
          domains.push(emailDomain);
        }
        return {
          ...prev,
          admin_name: prev.admin_name || name,
          admin_email: prev.admin_email || email,
          allowed_domains: domains,
        };
      });
    }
  }, [user]);

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<{
    organization_name: string;
    slug: string;
    status: string;
    message: string;
  } | null>(null);

  const [slugChecking, setSlugChecking] = useState(false);
  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "unavailable">("idle");
  const [slugFeedback, setSlugFeedback] = useState<{
    slug: string;
    available: boolean;
    org_name_exists: boolean;
    message?: string;
    suggestions: string[];
  } | null>(null);
  const [isCustomSlug, setIsCustomSlug] = useState(false);

  // Auto-slugify
  const handleNameChange = (name: string) => {
    const autoSlug = name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setFormData((prev) => ({
      ...prev,
      organization_name: name,
      slug: !isCustomSlug ? autoSlug : prev.slug,
    }));
  };

  const handleSlugChange = (rawSlug: string) => {
    setIsCustomSlug(true);
    const sanitized = rawSlug
      .toLowerCase()
      .replace(/[^\w-]/g, "")
      .replace(/--+/g, "-");
    setFormData((prev) => ({
      ...prev,
      slug: sanitized,
    }));
  };

  const handleSelectSuggestion = (suggestedSlug: string) => {
    setIsCustomSlug(true);
    setFormData((prev) => ({
      ...prev,
      slug: suggestedSlug,
    }));
  };

  // Real-time debounced slug availability verification
  useEffect(() => {
    const slugToCheck = formData.slug.trim();
    if (!slugToCheck || slugToCheck.length < 2) {
      setSlugStatus("idle");
      setSlugFeedback(null);
      setSlugChecking(false);
      return;
    }

    setSlugChecking(true);
    setSlugStatus("checking");

    const timer = setTimeout(async () => {
      try {
        const queryParams = new URLSearchParams({
          slug: slugToCheck,
          ...(formData.organization_name.trim() ? { org_name: formData.organization_name.trim() } : {}),
        });
        const res = await apiFetch(`/api/v1/b2b/check-slug?${queryParams.toString()}`);
        if (res.ok) {
          const data = await res.json();
          // Avoid stale state if user typed newer character
          if (formData.slug.trim() === slugToCheck) {
            setSlugFeedback(data);
            setSlugStatus(data.available ? "available" : "unavailable");
          }
        } else {
          setSlugStatus("idle");
        }
      } catch {
        setSlugStatus("idle");
      } finally {
        setSlugChecking(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [formData.slug, formData.organization_name]);

  const handleAddDomain = () => {
    const domain = formData.newDomainInput.trim().toLowerCase().replace("@", "");
    if (!domain) return;
    if (formData.allowed_domains.includes(domain)) return;
    setFormData((prev) => ({
      ...prev,
      allowed_domains: [...prev.allowed_domains, domain],
      newDomainInput: "",
    }));
  };

  const handleRemoveDomain = (domain: string) => {
    setFormData((prev) => ({
      ...prev,
      allowed_domains: prev.allowed_domains.filter((d) => d !== domain),
    }));
  };

  const handleAddSkill = () => {
    const skill = formData.newSkillInput.trim();
    if (!skill) return;
    if (formData.expected_skills.includes(skill)) return;
    setFormData((prev) => ({
      ...prev,
      expected_skills: [...prev.expected_skills, skill],
      newSkillInput: "",
    }));
  };

  const handleRemoveSkill = (skill: string) => {
    setFormData((prev) => ({
      ...prev,
      expected_skills: prev.expected_skills.filter((s) => s !== skill),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!formData.organization_name.trim()) {
      setErrorMsg("Please enter your organization name.");
      return;
    }

    if (!formData.slug.trim()) {
      setErrorMsg("Please enter a desired workspace URL.");
      return;
    }

    if (slugStatus === "unavailable") {
      setErrorMsg(
        slugFeedback?.message ||
          "This workspace URL already exists. Please choose a different URL or pick one of the suggestions below."
      );
      return;
    }

    if (slugChecking) {
      setErrorMsg("Please wait while we verify your workspace URL availability.");
      return;
    }

    const nameValidation = validateName(formData.admin_name, "Administrator name");
    if (!nameValidation.isValid) {
      setErrorMsg(nameValidation.error);
      return;
    }

    const emailValidation = validateEmail(formData.admin_email);
    if (!emailValidation.isValid) {
      setErrorMsg(emailValidation.error);
      return;
    }

    try {
      setSubmitting(true);
      const emailDomain = formData.admin_email.includes("@")
        ? formData.admin_email.split("@").pop()?.toLowerCase() || ""
        : "";
      const allowedDomains =
        formData.allowed_domains.length > 0
          ? formData.allowed_domains
          : emailDomain &&
            !["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com"].includes(
              emailDomain
            )
          ? [emailDomain]
          : [];

      const res = await apiFetch("/api/v1/b2b/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organization_name: formData.organization_name,
          slug: formData.slug || undefined,
          admin_name: formData.admin_name || "Admin",
          admin_email: formData.admin_email,
          industry: formData.industry,
          company_size: formData.company_size,
          allowed_domains: allowedDomains,
          expected_skills: formData.expected_skills,
          target_roles: formData.target_roles,
          departments: formData.departments,
          description: formData.description,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.error || data.detail || "Failed to submit organization registration.");
        return;
      }

      setSuccessData(data);
    } catch (err: any) {
      setErrorMsg("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col relative bg-transparent text-slate-900">
      {/* Persistent brand background - matching catalog and pricing pages */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(0,80,239,0.12),transparent_55%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none fixed -top-28 right-[-120px] -z-10 h-[620px] w-[620px] rounded-full bg-[#0050EF]/10 blur-[130px]"
      />
      <div
        aria-hidden
        className="pointer-events-none fixed top-[35vh] left-[-120px] -z-10 h-[520px] w-[520px] rounded-full bg-[#0050EF]/8 blur-[140px]"
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 bg-gradient-to-b from-white/0 via-white/40 to-white"
      />

      <Navbar />

      <div className="flex-1 py-10 md:py-14 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Back to B2B link */}
          <button
            onClick={() => navigate("/b2b")}
            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-6 transition group cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Back to B2B
          </button>

          {/* Success Modal / Banner */}
          <AnimatePresence>
            {successData &&
              (successData.status === "pending_activation" ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white border border-indigo-100 rounded-3xl p-8 sm:p-12 text-center shadow-xl shadow-indigo-100/40 mb-8"
                >
                  <div className="w-16 h-16 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-sm">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2.5">
                    Activation Link Dispatched!
                  </h2>
                  <p className="text-slate-600 text-sm max-w-lg mx-auto leading-relaxed mb-6">
                    We have sent an activation email to{" "}
                    <strong className="text-slate-900 font-semibold">{formData.admin_email}</strong>. Please check
                    your inbox and click the link to set your password and activate your{" "}
                    <strong className="text-slate-900 font-semibold">{successData.organization_name}</strong> enterprise
                    workspace.
                  </p>

                  <div className="p-5 bg-slate-50 border border-slate-200/80 rounded-2xl max-w-md mx-auto text-left text-xs space-y-2.5 mb-6 font-mono">
                    <div className="flex justify-between text-slate-500">
                      <span>Organization:</span>
                      <span className="text-slate-900 font-bold">{successData.organization_name}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Workspace Route:</span>
                      <span className="text-indigo-600 font-semibold">/org/{successData.slug}</span>
                    </div>
                    <div className="flex justify-between items-center text-slate-500">
                      <span>Status:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-700 font-bold uppercase text-[10px]">
                        Pending Activation
                      </span>
                    </div>
                  </div>

                  <div className="p-4 bg-indigo-50/70 border border-indigo-100 rounded-2xl max-w-md mx-auto text-xs text-indigo-900 mb-6 text-left leading-relaxed">
                    ✦ <strong>Instant Auto-Approval:</strong> As soon as you set your password via the emailed link,
                    your organization and team workspace will be automatically approved and ready for your team.
                  </div>

                  <button
                    onClick={() => navigate("/")}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl shadow-md shadow-indigo-600/20 transition cursor-pointer"
                  >
                    Return to Home
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white border border-emerald-100 rounded-3xl p-8 sm:p-12 text-center shadow-xl shadow-emerald-100/40 mb-8"
                >
                  <div className="w-16 h-16 bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-sm">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2.5">
                    Workspace Active!
                  </h2>
                  <p className="text-slate-600 text-sm max-w-lg mx-auto leading-relaxed mb-6">
                    Congratulations! <strong className="text-slate-900 font-semibold">{successData.organization_name}</strong>{" "}
                    is now registered and ready to empower your team.
                  </p>

                  <div className="p-5 bg-slate-50 border border-slate-200/80 rounded-2xl max-w-md mx-auto text-left text-xs space-y-2.5 mb-6 font-mono">
                    <div className="flex justify-between text-slate-500">
                      <span>Portal:</span>
                      <span className="text-indigo-600 font-semibold">/org/{successData.slug}/dashboard</span>
                    </div>
                    <div className="flex justify-between items-center text-slate-500">
                      <span>Status:</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold uppercase text-[10px]">
                        Active
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/org/${successData.slug}/dashboard`)}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl shadow-md shadow-indigo-600/20 transition cursor-pointer"
                  >
                    Go to Workspace Dashboard
                  </button>
                </motion.div>
              ))}
          </AnimatePresence>

          {!successData && (
            <div className="bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-10 md:p-12 shadow-xl shadow-slate-200/50 relative">
              {/* Header */}
              <div className="text-center mb-8 sm:mb-10">
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-indigo-50 border border-indigo-100/80 text-indigo-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-3.5 shadow-xs">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                  InaraX Enterprise Onboarding
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                  Register Your Organization
                </h1>
                <p className="text-slate-600 text-sm sm:text-base mt-2.5 max-w-lg mx-auto leading-relaxed">
                  Empower your workforce with customized AI upskilling tracks, diagnostic assessments, and centralized
                  competency analytics.
                </p>
              </div>

              {errorMsg && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200/80 rounded-2xl text-xs font-medium text-red-700 flex items-center gap-3">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 1. Company Info Section */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-600 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    1. Company Profile
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Company / Organization Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Acme Innovations"
                        value={formData.organization_name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 shadow-sm transition"
                      />
                      {slugFeedback?.org_name_exists && (
                        <p className="mt-1.5 text-[11px] text-amber-700 bg-amber-50/80 border border-amber-200/60 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5 leading-tight">
                          <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <span>An organization with this name already exists. Organization names can be shared, but your workspace URL must be unique.</span>
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          Desired Workspace URL <span className="text-red-500">*</span>
                        </label>
                        {slugChecking && (
                          <span className="text-[11px] text-indigo-600 flex items-center gap-1 font-medium animate-pulse">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Checking availability...
                          </span>
                        )}
                      </div>
                      <div
                        className={`flex items-center rounded-xl px-3.5 py-2.5 transition shadow-sm ${
                          slugStatus === "available"
                            ? "bg-emerald-50/30 border border-emerald-400 focus-within:bg-white focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/10"
                            : slugStatus === "unavailable"
                            ? "bg-red-50/40 border border-red-400 focus-within:bg-white focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/10"
                            : "bg-slate-50 border border-slate-200 focus-within:bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/10"
                        }`}
                      >
                        <span className="text-xs text-slate-400 mr-1 font-mono font-medium">/org/</span>
                        <input
                          type="text"
                          required
                          placeholder="acme-innovations"
                          value={formData.slug}
                          onChange={(e) => handleSlugChange(e.target.value)}
                          className="w-full bg-transparent text-sm text-slate-900 font-mono focus:outline-none placeholder-slate-400"
                        />
                        {slugStatus === "available" && (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 ml-1.5" />
                        )}
                        {slugStatus === "unavailable" && (
                          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 ml-1.5" />
                        )}
                      </div>

                      {/* Status feedback & Suggestions */}
                      {slugStatus === "available" && formData.slug && (
                        <p className="mt-1.5 text-xs text-emerald-600 font-medium flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          <span>Workspace URL is available: <strong className="font-mono">/org/{formData.slug}</strong></span>
                        </p>
                      )}

                      {slugStatus === "unavailable" && (
                        <div className="mt-2 p-3 bg-red-50/90 border border-red-200/90 rounded-xl space-y-2">
                          <div className="flex items-start gap-2 text-xs text-red-700 font-medium leading-relaxed">
                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                            <span>
                              Workspace URL <strong className="font-mono text-red-800">/org/{formData.slug}</strong> already exists. Please change it or pick an available suggestion below:
                            </span>
                          </div>

                          {slugFeedback?.suggestions && slugFeedback.suggestions.length > 0 && (
                            <div className="pt-1 border-t border-red-200/60">
                              <span className="block text-[11px] font-semibold text-slate-600 mb-1.5">
                                Available suggestions (click to use):
                              </span>
                              <div className="flex flex-wrap gap-1.5">
                                {slugFeedback.suggestions.map((sug) => (
                                  <button
                                    key={sug}
                                    type="button"
                                    onClick={() => handleSelectSuggestion(sug)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-white hover:bg-indigo-50 border border-slate-200 hover:border-indigo-300 text-indigo-700 rounded-lg text-xs font-mono font-medium transition cursor-pointer shadow-2xs hover:scale-[1.02]"
                                  >
                                    <Sparkles className="w-3 h-3 text-indigo-500" />
                                    <span>{sug}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Industry
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => setFormData((prev) => ({ ...prev, industry: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 shadow-sm transition"
                      >
                        {INDUSTRIES.map((ind) => (
                          <option key={ind} value={ind}>
                            {ind}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Company Size
                      </label>
                      <select
                        value={formData.company_size}
                        onChange={(e) => setFormData((prev) => ({ ...prev, company_size: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 shadow-sm transition"
                      >
                        {COMPANY_SIZES.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Organization Description <span className="text-slate-400 font-normal">(Optional)</span>
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Brief overview of your company, mission, or workforce training objectives..."
                      value={formData.description}
                      onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 shadow-sm transition resize-none"
                    />
                  </div>
                </div>

                {/* 2. Corporate Email Domain Restrictions */}
                <div className="pt-6 border-t border-slate-100 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-600 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />
                    2. Authorized Email Domains
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Specify your corporate email domains (e.g.{" "}
                    <code className="text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded font-mono">acme.com</code>). Only
                    employees with verified emails under these domains will be permitted to join.
                  </p>

                  {formData.allowed_domains.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.allowed_domains.map((dom) => (
                        <span
                          key={dom}
                          className="px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-800 rounded-xl text-xs font-mono font-medium flex items-center gap-2 shadow-xs"
                        >
                          <span>@{dom}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveDomain(dom)}
                            className="text-slate-400 hover:text-red-500 p-0.5 rounded-md hover:bg-slate-200/60 transition cursor-pointer"
                            title="Remove domain"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. company.com or company.io"
                      value={formData.newDomainInput}
                      onChange={(e) => setFormData((prev) => ({ ...prev, newDomainInput: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddDomain();
                        }
                      }}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 shadow-sm transition"
                    />
                    <button
                      type="button"
                      onClick={handleAddDomain}
                      className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shrink-0 shadow-sm transition cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Domain
                    </button>
                  </div>
                </div>

                {/* 3. Upskilling Goals & Target Roles */}
                <div className="pt-6 border-t border-slate-100 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-600 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    3. Expected Upskilling Goals & Skills
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Select or add the key skill tracks and technical focus areas your team will prioritize.
                  </p>

                  {formData.expected_skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.expected_skills.map((skill) => (
                        <span
                          key={skill}
                          className="px-3.5 py-1.5 bg-indigo-50 border border-indigo-200/70 text-indigo-700 rounded-full text-xs font-medium flex items-center gap-2 shadow-xs"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() => handleRemoveSkill(skill)}
                            className="text-indigo-400 hover:text-red-500 p-0.5 rounded-full hover:bg-indigo-100 transition cursor-pointer"
                            title="Remove skill"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add custom skill expectation..."
                      value={formData.newSkillInput}
                      onChange={(e) => setFormData((prev) => ({ ...prev, newSkillInput: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddSkill();
                        }
                      }}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 shadow-sm transition"
                    />
                    <button
                      type="button"
                      onClick={handleAddSkill}
                      className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shrink-0 shadow-sm transition cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Skill
                    </button>
                  </div>

                  {/* Quick suggested skill suggestions */}
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-[11px] text-slate-400 mr-1 font-medium">Suggestions:</span>
                    {SUGGESTED_SKILLS.filter((s) => !formData.expected_skills.includes(s))
                      .slice(0, 4)
                      .map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              expected_skills: [...prev.expected_skills, s],
                            }))
                          }
                          className="text-[11px] px-2.5 py-1 rounded-full border border-dashed border-slate-300 text-slate-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 transition cursor-pointer"
                        >
                          + {s}
                        </button>
                      ))}
                  </div>
                </div>

                {/* 4. Primary Administrator Info */}
                <div className="pt-6 border-t border-slate-100 space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-600 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      4. Primary Organization Administrator
                    </h3>
                    {isSignedIn && user && formData.admin_email === user.primaryEmailAddress?.emailAddress ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full text-[11px] font-medium">
                        <UserCheck className="w-3 h-3 text-emerald-600" />
                        Verified Clerk Account
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-full text-[11px] font-medium">
                        <Sparkles className="w-3 h-3 text-indigo-600" />
                        Public Registration
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Administrator Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Jane Doe"
                        value={formData.admin_name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, admin_name: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 shadow-sm transition"
                      />
                      {formData.admin_name &&
                        !validateName(formData.admin_name, "Administrator name").isValid && (
                          <p className="text-[11px] text-red-600 mt-1.5 flex items-center gap-1 font-medium">
                            <AlertCircle className="w-3 h-3 shrink-0" />
                            {validateName(formData.admin_name, "Administrator name").error}
                          </p>
                        )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5 flex items-center justify-between">
                        <span>
                          Work Email <span className="text-red-500">*</span>
                        </span>
                        {isSignedIn &&
                        user &&
                        formData.admin_email === user.primaryEmailAddress?.emailAddress ? (
                          <span className="text-[10px] text-emerald-600 flex items-center gap-1 font-medium">
                            <UserCheck className="w-2.5 h-2.5" /> Current user session
                          </span>
                        ) : (
                          <span className="text-[10px] text-indigo-600 flex items-center gap-1 font-medium">
                            <Sparkles className="w-2.5 h-2.5" /> Activation link will be emailed
                          </span>
                        )}
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="e.g. alex@acme.com"
                        value={formData.admin_email}
                        onChange={(e) => {
                          const email = e.target.value;
                          const domain = email.includes("@")
                            ? email.split("@").pop()?.toLowerCase() || ""
                            : "";
                          setFormData((prev) => {
                            const updatedDomains = [...prev.allowed_domains];
                            if (
                              updatedDomains.length === 0 &&
                              domain &&
                              ![
                                "gmail.com",
                                "yahoo.com",
                                "hotmail.com",
                                "outlook.com",
                                "icloud.com",
                              ].includes(domain)
                            ) {
                              updatedDomains.push(domain);
                            }
                            return {
                              ...prev,
                              admin_email: email,
                              allowed_domains: updatedDomains,
                            };
                          });
                        }}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 shadow-sm transition"
                      />
                      {formData.admin_email &&
                        !validateEmail(formData.admin_email).isValid && (
                          <p className="text-[11px] text-red-600 mt-1.5 flex items-center gap-1 font-medium">
                            <AlertCircle className="w-3 h-3 shrink-0" />
                            {validateEmail(formData.admin_email).error}
                          </p>
                        )}
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t border-slate-100">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 px-6 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 text-white font-semibold text-base rounded-xl shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2.5 transition transform active:scale-[0.99] cursor-pointer"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting Registration...
                      </>
                    ) : (
                      <>
                        Submit Organization Registration
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-slate-400 mt-3.5 flex items-center justify-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-slate-400" /> Enterprise workspace isolation & data encryption
                  </p>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </main>
  );
}
